import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';
import CategoryPage from './CategoryPage';
import TacoFaq from '../faqs/TacoFaq';

function TacoCategory() {
  const { city_name, vehicle_type, site_title } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();
  // CMS-driven copy. See Residential.jsx for the pattern. Seattle has no row
  // → falls through to hardcoded JSX. Austin has bespoke copy → renders CMS.
  const { content: cms } = usePageContent('cuisine_taco');

  const MainText = (
    <>
      <h1>{cms?.main?.heading || `Taco ${vehicleLowerCase}s in ${city_name}`}</h1>
      <CmsRichText
        html={cms?.main?.body}
        fallback={
          <p>Looking for al pastor, carne asada, birria, breakfast tacos, or fish tacos? Browse { city_name } taco { vehicleLowerCase }s, check menus and schedules, or request catering for an event.</p>
        }
      />
    </>
  );

  const SecondaryText = (
    <>
      <h3 className='u-phosphate'>{cms?.secondary?.heading || 'What you’ll find'}</h3>
      <CmsRichText
        html={cms?.secondary?.body}
        fallback={
          <p>Taco { vehicleLowerCase }s split roughly into street-style (corn tortillas, meat, onion, cilantro, salsa) and Tex-Mex (flour tortillas, cheese, breakfast tacos), with birria and fish tacos on both sides. Most sell by the taco, so ordering three or four is normal. Salsa heat varies a lot, so ask before you pour.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate u-mt4'>🚚 Seattle’s Must-Try Taco Trucks</h3>
          <p>Seattle’s food truck scene is packed with some of the best taco trucks in the Pacific Northwest, each bringing its own spin on the perfect taco. If you’re a birria lover, you can’t miss <Link to='/food-trucks/birrieria-pepe-el-toro'>Birrieria Pepe El Toro</Link>, where crispy, cheese-stuffed quesabirria tacos come with a rich dipping broth that enhances every bite. Looking for breakfast tacos? <Link to='/food-trucks/good-morning-tacos'>Good Morning Tacos</Link> serves up Austin-style morning flavors like smoked brisket, scrambled eggs, and hatch chile queso — perfect for an early start. Keeping it classic, <Link to='/food-trucks/plaza-garcia-express'>Plaza Garcia Express</Link> specializes in street-style carne asada, carnitas, and al pastor tacos, all topped with fresh cilantro and onions for an authentic experience.</p>
          <p>For a fusion twist, <Link to='/food-trucks/the-mobile-mayan'>The Mobile Mayan</Link> infuses BBQ flavors into Mexican classics, blending smoky meats with warm tortillas for a bold and unexpected taste. If you’re looking for something truly unique, <Link to='/food-trucks/tolu-modern-fijian-cuisine'>Tolu Modern Fijian Cuisine</Link> swaps traditional tortillas for poori frybread, filling them with Indo-Fijian chicken curry or pork for an unforgettable taco experience. Rounding out the list, <Link to='/food-trucks/qt-food-truck'>QT Food Truck</Link> focuses on fresh, high-quality ingredients in their tacos, burritos, and quesadillas, highlighting the simplicity of great Mexican flavors. With so many incredible taco trucks to choose from, Seattle’s street food scene is packed with creativity, variety, and flavor.</p>
        </>
      }

      <h3 className='u-phosphate u-mt4'>{cms?.events?.heading || 'Taco catering for your event'}</h3>
      <CmsRichText
        html={cms?.events?.body}
        fallback={
          <p>A taco bar with two or three proteins covers most crowds, vegetarians included, and guests build their own plates so the line moves. Tell us your date, location, and guest count. We’ll find { vehicleLowerCase }s that fit.</p>
        }
      />
    </>
  );

  return (
    <CategoryPage
      foodName='Tacos'
      foodCategory='mexican'
      seoTitle={cms?.meta?.title || `Taco ${vehicleLowerCase}s in ${city_name}`}
      seoDescription={cms?.meta?.description || `Find and book taco ${vehicleLowerCase}s in ${city_name}. Browse menus, compare quotes, and hire taco catering for your next event.`}
      seoCanonical='/taco-trucks'
      MainText={MainText}
      SecondaryText={SecondaryText}
      Faq={<TacoFaq />}
    />
  );
}

export default TacoCategory;
